import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Save, Plus, Trash2, ChevronUp, ChevronDown,
  Play, Film, BookOpen, Award, X, Search,
  Loader2, Edit2, ImageOff, Upload, FileCode,
} from "lucide-react";
import { api } from "../../api/client";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

const inp =
  "w-full bg-[#0E0F12] border border-white/10 rounded-xl px-4 py-3 text-sm " +
  "text-white focus:outline-none focus:border-[#CFFF50]/40 placeholder-gray-600";

function fmtSecs(s) {
  if (!s) return "";
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
}

// ── SCORM Upload Modal ────────────────────────────────────────────────────────
function ScormUploadModal({ courseId, onDone, onClose }) {
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState(null);
  const fileRef = useRef(null);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/courses/${courseId}/scorm/upload`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      const data = await res.json();
      onDone(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-[#111318] border border-white/10 rounded-3xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <FileCode size={16} className="text-[#CFFF50]" />
            <h2 className="font-bold text-lg">Import SCORM Package</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        <p className="text-xs text-gray-500 mb-4">
          Upload a <strong className="text-gray-400">.zip</strong> SCORM package. Supports SCORM 1.2 and SCORM 2004.
          The video inside the package should reference your Bunny.net URL.
        </p>

        <div
          className="border-2 border-dashed border-white/[0.1] rounded-2xl p-8 text-center cursor-pointer hover:border-[#CFFF50]/30 transition-colors mb-4"
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".zip"
            className="hidden"
            onChange={e => { setFile(e.target.files[0] || null); setError(null); }}
          />
          {file ? (
            <div>
              <FileCode size={24} className="mx-auto mb-2 text-[#CFFF50]" />
              <p className="text-sm font-semibold text-[#CFFF50]">{file.name}</p>
              <p className="text-xs text-gray-500 mt-1">{(file.size / 1024 / 1024).toFixed(1)} MB · click to change</p>
            </div>
          ) : (
            <div>
              <Upload size={28} className="mx-auto mb-3 text-gray-600" />
              <p className="text-sm text-gray-400">Click to select a SCORM .zip file</p>
              <p className="text-xs text-gray-600 mt-1">SCORM 1.2 and 2004 supported</p>
            </div>
          )}
        </div>

        {error && (
          <p className="text-red-400 text-xs mb-4 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-full border border-white/10 text-sm text-gray-400 hover:text-white transition-colors">
            Cancel
          </button>
          <button
            onClick={upload}
            disabled={!file || uploading}
            className="flex-1 py-2.5 rounded-full bg-[#CFFF50] text-[#0E0F12] text-sm font-bold hover:bg-[#d8ff6a] transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? <><Loader2 size={14} className="animate-spin" /> Importing…</> : "Import package"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bunny Video Picker Modal ──────────────────────────────────────────────────
function BunnyPickerModal({ existingIds, onAdd, onClose }) {
  const [query, setQuery]           = useState("");
  const [results, setResults]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [collections, setCollections] = useState([]);
  const [activeCol, setActiveCol]   = useState("");
  const [selected, setSelected]     = useState([]);
  const debounce = useRef(null);

  const search = useCallback(async (q, col) => {
    setLoading(true);
    try {
      const colParam = col ? `&collection_id=${encodeURIComponent(col)}` : "";
      const data = await api.get(`/admin/bunny/videos?q=${encodeURIComponent(q)}${colParam}`);
      setResults(Array.isArray(data) ? data : []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api.get("/admin/bunny/collections").then(c => Array.isArray(c) && setCollections(c)).catch(() => {});
  }, []);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => search(query, activeCol), 400);
  }, [query, activeCol, search]);

  const toggle = (v) =>
    setSelected(prev =>
      prev.find(s => s.id === v.id) ? prev.filter(s => s.id !== v.id) : [...prev, v]
    );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-[#111318] border border-white/10 rounded-3xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/[0.08]">
          <h2 className="font-bold text-lg">Add videos from Bunny</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={18} /></button>
        </div>

        {/* Collection tabs */}
        {collections.length > 0 && (
          <div className="flex gap-2 px-5 pt-4 flex-wrap">
            <button onClick={() => setActiveCol("")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${!activeCol ? "bg-[#CFFF50] text-[#0E0F12]" : "bg-white/[0.06] text-gray-400 hover:text-white"}`}>
              All videos
            </button>
            {collections.map(c => (
              <button key={c.id} onClick={() => setActiveCol(c.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${activeCol === c.id ? "bg-[#CFFF50] text-[#0E0F12]" : "bg-white/[0.06] text-gray-400 hover:text-white"}`}>
                {c.name}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="px-5 pt-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
            <input
              className="w-full bg-[#0a0b0e] border border-white/[0.08] rounded-xl pl-9 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#CFFF50]/30 placeholder-gray-600"
              placeholder="Search videos…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 size={24} className="animate-spin text-gray-600" />
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12 text-gray-600 text-sm">No videos found</div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {results.map(v => {
                const alreadyIn = existingIds.includes(v.id);
                const sel = selected.some(s => s.id === v.id);
                return (
                  <button key={v.id} onClick={() => !alreadyIn && toggle(v)} disabled={alreadyIn}
                    className={`relative rounded-xl overflow-hidden text-left transition-all ${alreadyIn ? "opacity-40 cursor-not-allowed" : sel ? "ring-2 ring-[#CFFF50]" : "hover:ring-1 hover:ring-white/20"}`}>
                    <div className="aspect-video bg-[#0a0b0e] relative">
                      {v.thumbnail
                        ? <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
                        : <div className="w-full h-full flex items-center justify-center"><Film size={20} className="text-gray-700" /></div>
                      }
                      {v.duration > 0 && (
                        <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">
                          {fmtSecs(v.duration)}
                        </span>
                      )}
                      {sel && (
                        <div className="absolute inset-0 bg-[#CFFF50]/20 flex items-center justify-center">
                          <Check size={24} className="text-[#CFFF50]" />
                        </div>
                      )}
                      {alreadyIn && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <span className="text-xs text-gray-400 font-semibold">Already added</span>
                        </div>
                      )}
                    </div>
                    <div className="p-2 bg-[#0E0F12]">
                      <p className="text-xs font-medium text-gray-300 line-clamp-2 leading-snug">{v.title}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-white/[0.08] flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {selected.length} video{selected.length !== 1 ? "s" : ""} selected
          </span>
          <button
            onClick={() => { onAdd(selected); onClose(); }}
            disabled={selected.length === 0}
            className="px-6 py-2.5 rounded-full bg-[#CFFF50] text-[#0E0F12] text-sm font-bold hover:bg-[#d8ff6a] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Add to course
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main CourseStudio ─────────────────────────────────────────────────────────
export default function CourseStudio() {
  const { id: courseId } = useParams();
  const navigate = useNavigate();

  const [course,           setCourse]           = useState(null);
  const [lessons,          setLessons]          = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [showPicker,       setShowPicker]       = useState(false);
  const [showScormUpload,  setShowScormUpload]  = useState(false);
  const [expanded,         setExpanded]         = useState(null);
  const [deleteConfirm,    setDeleteConfirm]    = useState(null);
  const [quizCounts,       setQuizCounts]       = useState({}); // lessonId → question count
  const [refreshing,       setRefreshing]       = useState(false);

  useEffect(() => { load(); }, [courseId]);

  const load = async () => {
    setLoading(true);
    try {
      const c = await api.get(`/admin/courses/${courseId}`);
      setCourse(c);
      const stages = c.curriculum || [];
      const mapped = stages.flatMap(s => (s.lessons || []).map(l => ({
        id:            l.id,
        title:         l.title || "Untitled",
        description:   l.description || "",
        duration:      l.duration || "",
        bunny_id:      l.blocks?.find(b => b.type === "video")?.data?.bunny_id || "",
        thumbnail:     l.thumbnail || "",
        embed_url:     l.video || l.blocks?.find(b => b.type === "video")?.data?.url || "",
        lesson_type:   l.lesson_type || "",
        scorm_id:      l.scorm_id || "",
        scorm_entry:   l.scorm_entry || "",
        scorm_version: l.scorm_version || "",
      })));
      setLessons(mapped);

    } catch (e) {
      toast.error("Could not load course");
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = (list) => ({
    stages: [{
      id:    `m_${courseId}`,
      stage: "Module 1: Course Content",
      lessons: list.map(l => ({
        id:          l.id,
        title:       l.title,
        description: l.description,
        type:        "lesson",
        duration:    l.duration,
        thumbnail:   l.thumbnail || "",
        video:       l.embed_url || "",
        blocks:      l.bunny_id ? [{
          id:   `b_${l.id}`,
          type: "video",
          data: { bunny_id: l.bunny_id, url: l.embed_url || "", title: l.title, completion_threshold: 90 },
        }] : [],
        ...(l.lesson_type === "scorm" ? {
          lesson_type:   "scorm",
          scorm_id:      l.scorm_id,
          scorm_entry:   l.scorm_entry,
          scorm_version: l.scorm_version,
        } : {}),
      })),
    }],
  });

  const save = async (list = lessons) => {
    setSaving(true);
    try {
      await api.put(`/admin/courses/${courseId}/curriculum`, buildPayload(list));
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const addVideos = async (videos) => {
    if (!videos.length) return;
    const added = videos.map(v => ({
      id:          `l_${v.id}`,
      title:       v.title || "Untitled",
      description: "",
      duration:    v.duration ? fmtSecs(v.duration) : "",
      bunny_id:    v.id,
      thumbnail:   v.thumbnail || "",
      embed_url:   v.embed_url || "",
    }));
    const next = [...lessons, ...added];
    setLessons(next);
    await save(next);
  };

  const toggleExpanded = (id) => {
    setExpanded(prev => {
      const next = prev === id ? null : id;
      if (next && quizCounts[id] === undefined) {
        api.get(`/admin/courses/${courseId}/lessons/${id}/quiz`)
          .then(d => setQuizCounts(p => ({ ...p, [id]: (d.questions || []).length })))
          .catch(() => setQuizCounts(p => ({ ...p, [id]: 0 })));
      }
      return next;
    });
  };

  const update   = (id, patch) => setLessons(prev => prev.map(l => l.id === id ? { ...l, ...patch } : l));
  const move     = (i, dir) => {
    const next = [...lessons];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setLessons(next);
  };
  const doDelete = async (id) => {
    const next = lessons.filter(l => l.id !== id);
    setLessons(next);
    setDeleteConfirm(null);
    if (expanded === id) setExpanded(null);
    await save(next);
  };

  const onScormDone = (data) => {
    setShowScormUpload(false);
    toast.success(`"${data.title}" imported as SCORM ${data.scorm_version} lesson`);
    load();
  };

  const existingBunnyIds = lessons.map(l => l.bunny_id).filter(Boolean);

  if (loading) return (
    <div className="min-h-screen bg-[#0E0F12] flex items-center justify-center">
      <Loader2 size={32} className="animate-spin text-[#CFFF50]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0E0F12] text-white">
      {/* Header */}
      <header className="h-14 flex items-center gap-4 px-6 border-b border-white/[0.08] bg-[#09090c] sticky top-0 z-10">
        <button onClick={() => navigate("/admin/courses")}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors pr-4 border-r border-white/[0.08]">
          <ArrowLeft size={14} /> Courses
        </button>
        <BookOpen size={14} className="text-gray-600" />
        <span className="text-sm font-semibold truncate flex-1">{course?.title || "Edit Course"}</span>
        {lessons.some(l => l.bunny_id) && (
          <button
            onClick={async () => {
              setRefreshing(true);
              try {
                await api.post(`/admin/courses/${courseId}/refresh-thumbnails`, {});
                await load();
                toast.success("Thumbnails refreshed");
              } catch { toast.error("Could not refresh thumbnails"); }
              finally { setRefreshing(false); }
            }}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-gray-400 text-xs font-semibold hover:border-white/20 hover:text-white transition-colors disabled:opacity-40"
          >
            {refreshing ? <Loader2 size={13} className="animate-spin" /> : <ImageOff size={13} />}
            {refreshing ? "Refreshing…" : "Refresh images"}
          </button>
        )}
        <button onClick={() => save()} disabled={saving}
          className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#CFFF50] text-[#0E0F12] text-sm font-bold hover:bg-[#d8ff6a] transition-colors disabled:opacity-40">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save"}
        </button>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Lessons header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-xl font-black tracking-tight">Course content</h2>
            <p className="text-sm text-gray-500 mt-0.5">{lessons.length} lesson{lessons.length !== 1 ? "s" : ""} · click a lesson to edit description or quiz</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowScormUpload(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-gray-300 text-sm font-semibold hover:border-white/20 hover:text-white transition-colors">
              <FileCode size={14} /> Import SCORM
            </button>
            <button onClick={() => setShowPicker(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#CFFF50] text-[#0E0F12] text-sm font-bold hover:bg-[#d8ff6a] transition-colors">
              <Plus size={14} /> Add videos
            </button>
          </div>
        </div>

        {/* Empty state */}
        {lessons.length === 0 && (
          <div className="text-center py-20 border-2 border-dashed border-white/[0.06] rounded-2xl">
            <Film size={36} className="mx-auto mb-3 text-gray-700" />
            <p className="text-gray-500 text-sm mb-5">No lessons yet</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => setShowScormUpload(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 text-gray-300 text-sm font-semibold hover:border-white/20 hover:text-white transition-colors">
                <FileCode size={14} /> Import SCORM
              </button>
              <button onClick={() => setShowPicker(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#CFFF50] text-[#0E0F12] text-sm font-bold hover:bg-[#d8ff6a] transition-colors">
                <Plus size={14} /> Add videos from Bunny
              </button>
            </div>
          </div>
        )}

        {/* Lesson cards */}
        <div className="space-y-3">
          {lessons.map((l, i) => {
            const isOpen = expanded === l.id;
            return (
              <div key={l.id} className="bg-[#111318] border border-white/[0.08] rounded-2xl overflow-hidden transition-all">
                {/* Row */}
                <div className="flex items-center gap-3 p-4">
                  {/* Thumbnail */}
                  <div className="w-20 h-12 rounded-lg overflow-hidden bg-black shrink-0 border border-white/[0.06]">
                    {l.lesson_type === "scorm" ? (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-[#0a0b0e] gap-1">
                        <FileCode size={14} className="text-[#CFFF50]" />
                        <span className="text-[9px] font-bold text-[#CFFF50] uppercase tracking-wider">SCORM</span>
                      </div>
                    ) : l.thumbnail ? (
                      <img src={l.thumbnail} alt={l.title} className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Play size={14} className="text-gray-600" /></div>
                    )}
                  </div>

                  {/* Title + duration */}
                  <div className="flex-1 min-w-0">
                    <input
                      value={l.title}
                      onChange={e => update(l.id, { title: e.target.value })}
                      className="w-full bg-transparent text-sm font-semibold text-white focus:outline-none border-b border-transparent focus:border-[#CFFF50]/40 transition-colors placeholder-gray-600 pb-0.5"
                    />
                    <div className="flex items-center gap-3 mt-1">
                      {l.lesson_type === "scorm" && (
                        <span className="text-[11px] font-bold text-[#CFFF50] uppercase tracking-wider">
                          SCORM {l.scorm_version}
                        </span>
                      )}
                      {l.duration && <span className="text-[11px] text-gray-600 font-mono">{l.duration}</span>}
                      {l.description && <span className="text-[11px] text-gray-600">Has description</span>}
                      {quizCounts[l.id] > 0 && (
                        <span className="text-[11px] font-semibold text-[#7C5CFF]">
                          Quiz · {quizCounts[l.id]}Q
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => move(i, -1)} disabled={i === 0}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-600 hover:text-white hover:bg-white/[0.06] disabled:opacity-20 transition-colors">
                      <ChevronUp size={13} />
                    </button>
                    <button onClick={() => move(i, 1)} disabled={i === lessons.length - 1}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-600 hover:text-white hover:bg-white/[0.06] disabled:opacity-20 transition-colors">
                      <ChevronDown size={13} />
                    </button>
                    <button onClick={() => toggleExpanded(l.id)}
                      className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isOpen ? "bg-[#7C5CFF]/20 text-[#7C5CFF]" : "text-gray-600 hover:text-white hover:bg-white/[0.06]"}`}>
                      <Edit2 size={13} />
                    </button>
                    <button onClick={() => setDeleteConfirm(l)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                {/* Expanded panel */}
                {isOpen && (
                  <div className="border-t border-white/[0.06] px-4 pb-5 pt-4 space-y-5">
                    {/* Description */}
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-2">
                        Lesson description
                      </label>
                      <textarea
                        value={l.description}
                        onChange={e => update(l.id, { description: e.target.value })}
                        rows={3}
                        placeholder="What will students learn in this lesson?"
                        className="w-full bg-[#0a0b0e] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-gray-300 focus:outline-none focus:border-[#CFFF50]/30 placeholder-gray-700 resize-none"
                      />
                    </div>

                    {/* Quiz */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Award size={13} className="text-[#7C5CFF]" />
                        <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                          Quiz
                        </label>
                        {quizCounts[l.id] > 0 && (
                          <span className="text-[10px] text-[#7C5CFF] font-semibold ml-1">
                            {quizCounts[l.id]} question{quizCounts[l.id] !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => navigate(`/admin/studio/quizzes/${courseId}/lessons/${l.id}`)}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#7C5CFF]/10 border border-[#7C5CFF]/20 text-sm font-semibold text-[#7C5CFF] hover:bg-[#7C5CFF]/15 transition-colors"
                      >
                        <Award size={14} />
                        {quizCounts[l.id] > 0 ? "Edit quiz in Quiz Studio" : "Add quiz in Quiz Studio"}
                      </button>
                    </div>

                    <div className="flex justify-end pt-1">
                      <button onClick={() => { save(); toggleExpanded(l.id); }}
                        className="px-6 py-2.5 rounded-full bg-[#CFFF50] text-[#0E0F12] text-sm font-bold hover:bg-[#d8ff6a] transition-colors">
                        Save & close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* SCORM upload modal */}
      {showScormUpload && (
        <ScormUploadModal
          courseId={courseId}
          onDone={onScormDone}
          onClose={() => setShowScormUpload(false)}
        />
      )}

      {/* Bunny picker modal */}
      {showPicker && (
        <BunnyPickerModal
          existingIds={existingBunnyIds}
          onAdd={addVideos}
          onClose={() => setShowPicker(false)}
        />
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#111318] border border-white/10 rounded-2xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-lg mb-2">Remove lesson?</h3>
            <p className="text-sm text-gray-400 mb-6">
              "{deleteConfirm.title}" will be removed from this course.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 rounded-full border border-white/10 text-sm text-gray-400 hover:text-white transition-colors">
                Cancel
              </button>
              <button onClick={() => doDelete(deleteConfirm.id)}
                className="flex-1 py-2.5 rounded-full bg-red-500/80 text-white text-sm font-bold hover:bg-red-500 transition-colors">
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
